import { useMemo } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { alpha, useTheme } from "@mui/material/styles";
import type { IPagamentoLoja, IPedidoDetalhe } from "interfaces/loja";
import { resolverInteligenciaPedidoDetalhe } from "utils/pedidoInteligenciaOperacional";
import {
  extrairPosVendaDoHistorico,
  labelTipoPosVenda,
} from "utils/pedidoPosVenda";

export type PedidoInteligenciaOperacionalProps = {
  detalhe: IPedidoDetalhe;
  pagamentos: IPagamentoLoja[];
};

function scrollParaSecao(anchorId: string) {
  document.getElementById(anchorId)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function chipSaudeProps(saude: "saudavel" | "atencao" | "critico") {
  switch (saude) {
    case "critico":
      return { label: "Crítico", color: "error" as const };
    case "atencao":
      return { label: "Atenção", color: "warning" as const };
    default:
      return { label: "Saudável", color: "success" as const };
  }
}

export default function PedidoInteligenciaOperacional({
  detalhe,
  pagamentos,
}: PedidoInteligenciaOperacionalProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const intel = useMemo(
    () => resolverInteligenciaPedidoDetalhe(detalhe, pagamentos),
    [detalhe, pagamentos]
  );

  const posVendaInfo = useMemo(
    () => extrairPosVendaDoHistorico(detalhe.historicoPedidos),
    [detalhe.historicoPedidos]
  );

  const chip = chipSaudeProps(intel.saude);

  const temAlertas =
    intel.alertasCriticos.length > 0 ||
    intel.alertasAtencao.length > 0 ||
    intel.alertasSucesso.length > 0;
  const temSugestoes = intel.sugestoes.length > 0;

  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(
          theme.palette.background.paper,
          mode === "dark" ? 0.55 : 1
        ),
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="button" fontWeight={800} color="text.primary">
            Inteligência operacional
          </Typography>

          <Chip
            size="small"
            variant="filled"
            color={chip.color}
            sx={{ fontWeight: 700, borderWidth: 1, padding: 2 }}
            label={`Saúde: ${chip.label}`}
          />
        </Stack>

        <Stack spacing={1}>
          {posVendaInfo?.origemCliente &&
          String(detalhe.status ?? "").toUpperCase() ===
            "DEVOLUCAO_SOLICITADA" ? (
            <Alert severity="info" variant="filled">
              <Typography variant="body2" fontWeight={700}>
                {labelTipoPosVenda(posVendaInfo.tipo)} pelo cliente
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {posVendaInfo.protocolo
                  ? `Protocolo ${posVendaInfo.protocolo}. `
                  : ""}
                {posVendaInfo.tipo === "TROCA"
                  ? "Após confirmar a devolução, trate a troca manualmente (novo envio ou pedido cortesia)."
                  : "Analise o motivo e confirme a devolução para estorno e reposição de estoque."}
              </Typography>
            </Alert>
          ) : null}
          {!temAlertas ? (
            <Alert severity="info" variant="outlined">
              Nenhum sinal de SLA ou pagamento a destacar — use a linha do tempo
              e o histórico para detalhes.
            </Alert>
          ) : null}
          {intel.alertasCriticos.map((a) => (
            <Alert key={a.id} severity="error" variant="filled">
              <Typography variant="body2" fontWeight={700}>
                {a.titulo}
              </Typography>
              {a.detalhe ? (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ opacity: 0.95, mt: 0.5 }}
                >
                  {a.detalhe}
                </Typography>
              ) : null}
            </Alert>
          ))}
          {intel.alertasAtencao.map((a) => (
            <Alert key={a.id} severity="warning" variant="outlined">
              <Typography variant="body2" fontWeight={700}>
                {a.titulo}
              </Typography>
              {a.detalhe ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={0.5}
                >
                  {a.detalhe}
                </Typography>
              ) : null}
            </Alert>
          ))}
          {intel.alertasSucesso.map((a) => (
            <Alert key={a.id} severity="success" variant="outlined">
              <Typography variant="body2" fontWeight={700}>
                {a.titulo}
              </Typography>
              {a.detalhe ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={0.5}
                >
                  {a.detalhe}
                </Typography>
              ) : null}
            </Alert>
          ))}
        </Stack>

        {temSugestoes ? (
          <Box
            sx={{
              pt: 0.5,
              borderTop: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              display="block"
              mb={1}
            >
              Sugestões automáticas
            </Typography>
            <Stack spacing={1}>
              {intel.sugestoes.map((s) => (
                <Typography key={s.id} variant="body2" color="text.secondary">
                  • {s.texto}{" "}
                  {s.anchorId ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => scrollParaSecao(s.anchorId!)}
                      sx={{ verticalAlign: "baseline", fontWeight: 700 }}
                    >
                      Ir para ações
                    </Link>
                  ) : null}
                </Typography>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
